import { ApolloClient, ApolloLink, FieldPolicy, InMemoryCache, makeVar } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { relayStylePagination } from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import Cookies from 'js-cookie'
import { TypedTypePolicies } from './generated/apollo-helpers'
import { LOCAL_STORAGE_KEYS } from './constants'

export const isWholesaleModeVar = makeVar(Boolean(localStorage.getItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)))

// Space (px) currently reserved below the header (0 when the header is not sticky,
// shrinks while the header is sliding out of view on scroll-down). Sticky sidebars
// elsewhere on the page read this instead of hardcoding the header's height.
export const headerOffsetVar = makeVar(0)

// Whether the change above is a smooth slide (hidden<->shown within sticky mode, which
// the header itself also animates) vs an instant cut (entering/leaving sticky mode,
// where the header swaps position:relative<->fixed with no transition of its own) -
// sticky sidebars should only animate their own offset in the first case, or they lag
// behind the header's instant jump instead of moving with it.
export const headerOffsetAnimatedVar = makeVar(false)

export function findValueInEnum(needle, haystack) {
  // const match = Object.entries(haystack).find(([_, value]) => value === needle);
  //
  // if (!match) {
  //   throw new Error(`Value ${needle} not found in enum`);
  // }
  return needle
}

// const setCookieCsrf = async () => {
//     const response = await fetch('/get-csrf/');
//     const data = await response.json();
//     Cookies.set('csrftoken', data.csrf);
// };

// const getCookieCsrf = async () => {
//     const csrf = Cookies.get('csrftoken');
//     console.log('get csrf', csrf);
//     if (csrf) {
//         return csrf;
//     }
//     await setCookieCsrf();
//     return Cookies.get('csrftoken');
// };

// Offset-paginated `cartLines` ({ totalCount, results }) for "Show More" lists: every page of one
// cartType/filters combination is stored in a single list, so fetchMore appends to what is already shown.
// A request from offset 0 (first load, or refetchQueries: ['CartLines'] reloading the whole shown range)
// replaces the list, dropping lines removed in the meantime.
const cartLinesOffsetPagination: FieldPolicy = {
  keyArgs: ['cartType', 'filters'],
  merge(existing, incoming, { args, readField }) {
    const offset = args?.pagination?.offset ?? 0
    const head = offset > 0 ? (existing?.results ?? []).slice(0, offset) : []
    const headIds = new Set(head.map(line => readField('id', line)))
    return {
      ...incoming,
      // Skip lines the shown range already holds, should lines have been added before it since it loaded.
      results: [...head, ...incoming.results.filter(line => !headIds.has(readField('id', line)))],
    }
  },
  read(existing, { args }) {
    if (!existing) return undefined
    const offset = args?.pagination?.offset ?? 0
    const limit = args?.pagination?.limit ?? existing.results.length
    return { ...existing, results: existing.results.slice(offset, offset + limit) }
  },
}

const typePolicies: TypedTypePolicies = {
  Query: {
    fields: {
      specifics: relayStylePagination(['filters', 'ordering']),
      balanceTransactions: relayStylePagination([]),
      socialPosts: relayStylePagination(['filters', 'ordering']),
      productVariants: relayStylePagination(['filters']),
      cartLines: cartLinesOffsetPagination,
      blogArticleList: relayStylePagination(['filters', 'first']),
    },
  },
  FoundSpecificsType: {
    fields: {
      specifics: relayStylePagination(['filters']),
    },
  },
  CartType: {
    fields: {
      // CartTotalPriceType is normalized by cart id whatever promo code it was computed with, so keep one
      // field entry too - that lets mutations returning `totalPrice` update every query that reads it.
      totalPrice: { keyArgs: false },
      allLines: {
        merge: (_, incoming) => {
          return [...(incoming || [])]
        },
      },
      lines: relayStylePagination(['cartType']),
    },
  },
  UserType: {
    fields: {
      patternTransactions: relayStylePagination(['patternType']),
    },
  },
}

// Stale keys left behind by the removed apollo3-cache-persist (the whole cache used to be mirrored into
// localStorage and restored on startup, which blocked the first render and served stale carts/prices).
const LEGACY_CACHE_PERSIST_KEYS = ['apollo-cache-persist', 'APOLLO_SCHEMA_VERSION']

const createClient = async () => {
  const cache = new InMemoryCache({
    typePolicies,
  })
  LEGACY_CACHE_PERSIST_KEYS.forEach(key => localStorage.removeItem(key))

  const JWTError = {
    invalid: 'JSONWebTokenError',
    expired: 'JSONWebTokenExpired',
  }

  function isJwtError(error) {
    return !!findValueInEnum(error?.message, JWTError)
  }

  const invalidTokenLink = onError(({ graphQLErrors, networkError }) => {
    if (networkError || graphQLErrors?.some(isJwtError)) {
      Cookies.remove('csrftoken', { path: '' })
    }
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      )

    if (networkError) console.log(`[Network error]: ${networkError}`)
  })

  const authLink = setContext((req, context) => {
    // const csrftoken = await getCookieCsrf();
    const csrftoken = Cookies.get('csrftoken') || ''
    if (csrftoken) {
      return {
        ...context,
        headers: {
          ...context.headers,
          'X-CSRFToken': csrftoken,
        },
      }
    } else {
      return context
    }
  })
  const linkOptions = {
    credentials: 'same-origin',
    uri: '/strawberry/graphql',
  }
  const uploadLink = createUploadLink(linkOptions)
  const batchLink = new BatchHttpLink({
    batchInterval: 10,
    batchMax: 3,
    ...linkOptions,
  })
  const link = ApolloLink.split(operation => !operation.getContext().notBatching, batchLink, uploadLink)

  return new ApolloClient({
    connectToDevTools: process.env.NODE_ENV === 'development',
    link: invalidTokenLink.concat(authLink.concat(link)),
    cache,
  })

  // client.onResetStore(async () => {
  //   await addDefaultsToCache(cache)
  // });
}
export default createClient
